import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import {
    VERIFICATION_RESULT,
    VERIFICATION_STATUS,
    MIC_FAILURE_TYPES,
} from '../TROInstructionsConstants';
import TRCButton from '../../../components/TRCButton';
import RecordingContainer from '../../../../common/recording/RecordingContainer';
import GoButtonDark from '../../../../common/components/GoButtonDark';
import { ARIA_LABELS } from '../../../../constants/text';

const MAX_RECORDING_TIME_MS = 30000;
class MicCard extends Component {
    constructor(props) {
        super(props);
        this.recorderRef = React.createRef();
        this.state = {
            isOverlayActive: true,
            isRecordingClicked: false,
            isProcessingRecording: false,
        };
    }

    componentDidUpdate(prevProps) {
        const {
            verificationStatus,
            isSpinnerVisible,
            recordingStream,
            onFailure,
            recordedResult,
            shouldReinitializeRecorder,
            isRecordingInProgress,
        } = this.props;
        const hasStatusChanged = verificationStatus !== prevProps.verificationStatus;
        const hasSpinnerVisibilityChanged = isSpinnerVisible !== prevProps.isSpinnerVisible;
        const hasRecordedResultChanged = recordedResult !== prevProps.recordedResult;
        const hasRecordingStoppedChanged =
            prevProps.isRecordingInProgress && !isRecordingInProgress;

        const isPassed = this.isPassed();

        // Reinitialize recorder when requested (after mic test failures)
        if (shouldReinitializeRecorder && !prevProps.shouldReinitializeRecorder) {
            this.reinitializeRecorder();
        }

        // Reset recording state when recording stops (either manually or via timeout)
        if (hasRecordingStoppedChanged && this.state.isRecordingClicked) {
            this.setState({
                isRecordingClicked: false,
                isProcessingRecording: true,
            });
        }

        // Reset processing state when recording is saved to Redux
        if (hasRecordedResultChanged && recordedResult && this.state.isProcessingRecording) {
            this.setState({ isProcessingRecording: false });
        }

        if (hasSpinnerVisibilityChanged && isSpinnerVisible) this.setOverlayToInactive();

        if (hasSpinnerVisibilityChanged && !isSpinnerVisible && !isPassed)
            this.setOverlayToActive();

        if (hasStatusChanged && isPassed) {
            this.setOverlayToInactive();
        }

        if (recordingStream && !recordingStream.active) {
            onFailure(MIC_FAILURE_TYPES.ACCESS_FAILED);
        } else if (hasStatusChanged && verificationStatus === VERIFICATION_STATUS.ERROR) {
            onFailure(MIC_FAILURE_TYPES.MIC_TEST_ERROR);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    setOverlayToInactive = () => this.setState({ isOverlayActive: false });

    setOverlayToActive = () => this.setState({ isOverlayActive: true });

    isPassed = () => {
        const { verificationResult, isPassed } = this.props;
        return isPassed || verificationResult === VERIFICATION_RESULT.PASSED;
    };

    reinitializeRecorder = () => {
        if (this.recorderRef.current) {
            this.recorderRef.current.reinitializeRecorder();
        }
    };

    onStopRecordingClick = isExternalURLSourcePresent => {
        if (!this.state.isRecordingClicked) {
            return;
        }
        this.setState(
            {
                isRecordingClicked: false,
                isProcessingRecording: true,
            },
            () => {
                this.recorderRef.current.stopRecording(() => {
                    this.props.onStopRecording(isExternalURLSourcePresent);
                });
            },
        );
    };

    onStartRecordingClick = isExternalURLSourcePresent => {
        if (this.state.isRecordingClicked) {
            return;
        }
        const { updateVerificationProgress } = this.props;
        updateVerificationProgress(isExternalURLSourcePresent);

        this.setState(
            {
                isRecordingClicked: true,
            },
            () => {
                this.recorderRef.current.startRecording();
            },
        );
    };

    isVisualizerActive = () => this.props.isRecordingInProgress && this.props.recordingStream;

    render = () => {
        const {
            isRecordingInProgress,
            isScreenBlocked,
            saveRecordedResult,
            isSpinnerVisible,
            onArrowButtonClick,
            children,
            hideArrowButton,
            isExternalURLSourcePresent,
        } = this.props;
        const { isOverlayActive, isProcessingRecording } = this.state;
        const isPassed = this.isPassed();
        return (
            <React.Fragment>
                {isOverlayActive && <div className="reading-overlay" />}
                <div className="mic-test-card">
                    <RecordingContainer
                        ref={this.recorderRef}
                        duration={MAX_RECORDING_TIME_MS}
                        saveRecordedResult={saveRecordedResult}
                    />
                    {!isRecordingInProgress &&
                        !isSpinnerVisible &&
                        !isProcessingRecording &&
                        isOverlayActive && (
                            <TRCButton
                                ariaLabel={ARIA_LABELS.START_RECORDING}
                                onClick={() =>
                                    this.onStartRecordingClick(isExternalURLSourcePresent)
                                }
                                text="Start recording"
                                styles={classNames('start-recording-button', {
                                    disabled: isScreenBlocked,
                                })}
                                isEnabled={!isScreenBlocked && !this.state.isRecordingClicked}
                            />
                        )}
                    {isRecordingInProgress && isOverlayActive && (
                        <TRCButton
                            ariaLabel={ARIA_LABELS.STOP_RECORDING}
                            onClick={() => this.onStopRecordingClick(isExternalURLSourcePresent)}
                            text="Stop recording"
                            styles="stop-recording-button"
                            isEnabled={!isScreenBlocked && this.state.isRecordingClicked}
                        />
                    )}
                    {children}
                </div>
                {!hideArrowButton && isPassed && (
                    <GoButtonDark
                        ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                        onClick={onArrowButtonClick}
                        isPulsating={!isScreenBlocked}
                        isEnabled={!isScreenBlocked}
                    />
                )}
            </React.Fragment>
        );
    };
}

MicCard.propTypes = {
    recordingStream: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(MediaStream)]),
    isRecordingInProgress: PropTypes.bool.isRequired,
    isScreenBlocked: PropTypes.bool.isRequired,
    verificationResult: PropTypes.string,
    onArrowButtonClick: PropTypes.func,
    verificationStatus: PropTypes.string,
    isSpinnerVisible: PropTypes.bool.isRequired,
    cleanup: PropTypes.func.isRequired,
    updateVerificationProgress: PropTypes.func,
    onStopRecording: PropTypes.func,
    saveRecordedResult: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    children: PropTypes.node,
    hideArrowButton: PropTypes.bool,
    isPassed: PropTypes.bool,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    recordedResult: PropTypes.oneOfType([
        PropTypes.instanceOf(Blob),
        PropTypes.oneOf([null, undefined]),
    ]),
    shouldReinitializeRecorder: PropTypes.bool,
};

MicCard.defaultProps = {
    recordingStream: false,
    children: <React.Fragment />,
    updateVerificationProgress: () => {},
    onStopRecording: () => {},
    onArrowButtonClick: () => {},
    hideArrowButton: false,
    verificationStatus: VERIFICATION_STATUS.IDLE,
    isPassed: false,
    verificationResult: VERIFICATION_RESULT.UNAVAILABLE,
    recordedResult: null,
    shouldReinitializeRecorder: false,
};

export default MicCard;
